import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInnovator, AccountActions } from 'modules/account';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { UiActions } from 'modules/ui';
import {
  YEAR_OF_BIRTH_DEFAULT,
  MONTH_OF_BIRTH_DEFAULT,
  JAPAN_PREFECTURE_ID_DEFAULT,
  PHONE_COUNTRY_ID_DEFAULT,
  ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
  ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
  ADDRESS_OVERSEAS_ID,
} from 'constants/profile';
import { NO_SELECT_NUMBER } from 'constants/form';
import apiInnovator from 'external/api/innovator';
import { UpdateInnovatorRequest } from 'proto/v1/innovatorservice/innovatorservice';
import {
  NewGraduateProfileBasicInfoFormValues,
  ProfileFormItems,
} from 'types/profile';
import convertToInnovatorToUpdate from 'utils/profile/convertToInnovatorToUpdate';
import {
  getDefaultPhoneCountryIdValue,
  getDefaultAcademicBackgroundSchoolValue,
  getDefaultNewGraduateResearchRoomsValue,
} from 'utils/profile/defaultFormValues';
import {
  getPhoneCountryCodeValueToUpdate,
  getJapanPrefectureValueToUpdate,
  getNewGraduateResearchRoomsValueToUpdate,
} from 'utils/profile/formValuesToUpdate';
import {
  lastNameSchema,
  firstNameSchema,
  lastNameKanaSchema,
  firstNameKanaSchema,
  yearOfBirthSchema,
  monthOfBirthSchema,
  phoneNumberSchema,
  phoneCountryIdSchema,
  japanPrefectureIdSchema,
  academicBackgroundSchema,
  careerAnnualIncomeIdSchema,
  careerMonthlyUnitPriceSchema,
} from 'utils/profile/schema';
import RequestError from 'classes/RequestError';
import useAcademicBackgroundSchoolFormField from 'hooks/profile/useAcademicBackgroundSchoolFormField';
import useErrorNotification from 'hooks/useErrorNotification';
import useAutoCompleteKanaName from 'hooks/profile/useAutoCompleteKanaName';

const schema = object().shape({
  lastName: lastNameSchema,
  firstName: firstNameSchema,
  lastNameKana: lastNameKanaSchema,
  firstNameKana: firstNameKanaSchema,
  yearOfBirth: yearOfBirthSchema,
  monthOfBirth: monthOfBirthSchema,
  phoneNumber: phoneNumberSchema,
  phoneCountryId: phoneCountryIdSchema,
  japanPrefectureId: japanPrefectureIdSchema,
  academicBackground: academicBackgroundSchema,
  career: object().shape({
    annualIncomeId: careerAnnualIncomeIdSchema,
    monthlyUnitPrice: careerMonthlyUnitPriceSchema,
  }),
});

const defaultValues = {
  lastName: '',
  firstName: '',
  lastNameKana: '',
  firstNameKana: '',
  yearOfBirth: YEAR_OF_BIRTH_DEFAULT,
  monthOfBirth: MONTH_OF_BIRTH_DEFAULT,
  phoneNumber: '',
  phoneCountryId: PHONE_COUNTRY_ID_DEFAULT,
  japanPrefectureId: JAPAN_PREFECTURE_ID_DEFAULT,
  career: {
    annualIncomeId: NO_SELECT_NUMBER,
  },
  academicBackground: {
    school: null,
    faculty: '',
    departmentId: NO_SELECT_NUMBER,
    yearOfGraduation: ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
    monthOfGraduation: ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
  },
};

type Payload = {
  formItems: ProfileFormItems;
  onShowContent: () => void;
  onOpenCompletedModal: () => void;
};

const useNewGraduateProfileBasicInfoForm = ({
  formItems,
  onShowContent,
  onOpenCompletedModal,
}: Payload) => {
  const dispatch = useDispatch();

  const { handleRequestError } = useErrorNotification();

  const innovator = useSelector(getInnovator);

  const innovatorToUpdate = useMemo(
    () => innovator && convertToInnovatorToUpdate(innovator),
    [innovator],
  );

  const onSubmit = useCallback(
    async (values: NewGraduateProfileBasicInfoFormValues) => {
      const valuesToUpdate: UpdateInnovatorRequest = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...innovatorToUpdate!,
        lastName: values.lastName,
        firstName: values.firstName,
        lastNameKana: values.lastNameKana,
        firstNameKana: values.firstNameKana,
        yearOfBirth: values.yearOfBirth,
        monthOfBirth: values.monthOfBirth,
        phoneNumber: values.phoneNumber,
        phoneCountryCode: getPhoneCountryCodeValueToUpdate(
          values.phoneCountryId,
          formItems.countries,
        ),
        japanPrefectureId: getJapanPrefectureValueToUpdate(
          values.japanPrefectureId,
        ),
        academicBackground: {
          schoolId: values.academicBackground.school?.value ?? NO_SELECT_NUMBER,
          faculty: values.academicBackground?.faculty ?? '',
          departmentId:
            values.academicBackground?.departmentId ?? NO_SELECT_NUMBER,
          yearOfGraduation:
            values.academicBackground?.yearOfGraduation ??
            ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
          monthOfGraduation:
            values.academicBackground?.monthOfGraduation ??
            ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
        },
        newGraduate: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...innovatorToUpdate!.newGraduate!,
          researchRooms: getNewGraduateResearchRoomsValueToUpdate(
            values.newGraduate.researchRooms,
          ),
        },
      };

      let data;
      try {
        ({ data } = await apiInnovator.updateInnovator(valuesToUpdate));
      } catch (error: unknown) {
        if (error instanceof RequestError) {
          dispatch(UiActions.setLoading(false));
          handleRequestError(error);
        }
        return;
      }
      dispatch(AccountActions.setInnovator(data.innovator));
      dispatch(UiActions.setLoading(false));
      onShowContent();
      onOpenCompletedModal();
    },
    [
      dispatch,
      formItems.countries,
      handleRequestError,
      innovatorToUpdate,
      onOpenCompletedModal,
      onShowContent,
    ],
  );

  const formMethods = useForm<NewGraduateProfileBasicInfoFormValues>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset } = formMethods;

  useAutoCompleteKanaName(formMethods);

  const {
    schoolOptionsState,
    handleInputChangeSchool,
    handleCreateSchoolOption,
    fetchMoreSchoolOptions,
  } = useAcademicBackgroundSchoolFormField({ formMethods });

  // Set default values
  useEffect(() => {
    if (!innovator || !innovatorToUpdate) return;

    const values: NewGraduateProfileBasicInfoFormValues = {
      lastName: innovatorToUpdate.lastName,
      firstName: innovatorToUpdate.firstName,
      lastNameKana: innovatorToUpdate.lastNameKana,
      firstNameKana: innovatorToUpdate.firstNameKana,
      yearOfBirth: innovatorToUpdate.yearOfBirth,
      monthOfBirth: innovatorToUpdate.monthOfBirth,
      phoneNumber: innovatorToUpdate.phoneNumber,
      phoneCountryId: getDefaultPhoneCountryIdValue(
        innovatorToUpdate.phoneCountryCode,
        formItems.countries,
      ),
      japanPrefectureId:
        innovatorToUpdate.japanPrefectureId ?? ADDRESS_OVERSEAS_ID,
      academicBackground: {
        school: getDefaultAcademicBackgroundSchoolValue(
          innovator.academicBackground?.school,
        ),
        faculty: innovatorToUpdate.academicBackground?.faculty ?? '',
        departmentId:
          innovatorToUpdate.academicBackground?.departmentId ??
          NO_SELECT_NUMBER,
        yearOfGraduation:
          innovatorToUpdate.academicBackground?.yearOfGraduation ??
          ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
        monthOfGraduation:
          innovatorToUpdate.academicBackground?.monthOfGraduation ??
          ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
      },
      newGraduate: {
        researchRooms: getDefaultNewGraduateResearchRoomsValue(
          innovatorToUpdate.newGraduate?.researchRooms,
        ),
      },
    };

    reset(values);
  }, [formItems.countries, innovator, innovatorToUpdate, reset]);

  return {
    innovator,
    formMethods,
    onSubmit,
    schoolOptionsState,
    handleInputChangeSchool,
    handleCreateSchoolOption,
    fetchMoreSchoolOptions,
  };
};

export default useNewGraduateProfileBasicInfoForm;
